import React from 'react';
import { Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { exportToCSV } from '../../../services/exportService';

const ExecutionDrivers = ({ onShowEvidence }) => {
  const throughputData = [
    { month: "Aug 2025", output: 1250, target: 1200 },
    { month: "Sep 2025", output: 1320, target: 1250 },
    { month: "Oct 2025", output: 1180, target: 1300 },
    { month: "Nov 2025", output: 1410, target: 1350 },
    { month: "Dec 2025", output: 1380, target: 1400 },
    { month: "Jan 2026", output: 1450, target: 1450 }
  ];

  const completionData = [
    { category: "On Time", value: 72, count: 864 },
    { category: "Delayed", value: 18, count: 216 },
    { category: "At Risk", value: 10, count: 120 }
  ];

  const delayTrend = [
    { week: "Week 1", delays: 12, avgDelay: 2.3 },
    { week: "Week 2", delays: 15, avgDelay: 3.1 },
    { week: "Week 3", delays: 18, avgDelay: 3.8 },
    { week: "Week 4", delays: 14, avgDelay: 2.9 }
  ];

  const bottleneckAreas = [
    {
      area: "Quality Assurance",
      impact: "High",
      delayDays: 4.5,
      affectedProjects: 23,
      description: "Testing backlog causing significant delays in project delivery timelines",
      action: "Increase QA team capacity and implement automated testing"
    },
    {
      area: "Client Approvals",
      impact: "Medium",
      delayDays: 3.2,
      affectedProjects: 18,
      description: "Extended approval cycles from client stakeholders delaying project progression",
      action: "Streamline approval process and set clear SLAs"
    },
    {
      area: "Resource Allocation",
      impact: "Medium",
      delayDays: 2.8,
      affectedProjects: 15,
      description: "Delays in assigning specialized resources to critical project phases",
      action: "Improve resource planning and cross-training initiatives"
    }
  ];

  const reworkMetrics = {
    totalRework: 156,
    reworkRate: 13.2,
    costImpact: 285000,
    timeImpact: 342,
    topReasons: [
      { reason: "Unclear Requirements", percentage: 35, count: 55 },
      { reason: "Technical Issues", percentage: 28, count: 44 },
      { reason: "Design Changes", percentage: 22, count: 34 },
      { reason: "Quality Issues", percentage: 15, count: 23 }
    ]
  };

  const getImpactColor = (impact) => {
    if (impact === 'High') return 'var(--color-error)';
    if (impact === 'Medium') return 'var(--color-warning)';
    return 'var(--color-success)';
  };

  const projectHealth = [
    { status: "On Track", count: 45, percentage: 78.9, color: "#38A169" },
    { status: "At Risk", count: 8, percentage: 14.0, color: "#D69E2E" },
    { status: "Delayed", count: 3, percentage: 5.3, color: "#DC2626" },
    { status: "On Hold", count: 1, percentage: 1.8, color: "#718096" }
  ];

  const handleExportCSV = () => {
    const projectData = projectHealth?.map(item => ({
      Status: item?.status,
      Count: item?.count,
      'Percentage %': item?.percentage
    }));

    exportToCSV({
      title: 'Execution_Project_Health',
      data: projectData
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-md bg-primary/10">
            <Icon name="Activity" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">Execution & Delivery</h2>
            <p className="text-sm text-muted-foreground font-caption">Project health and delivery performance metrics</p>
          </div>
        </div>
        <Button
          onClick={handleExportCSV}
          variant="outline"
          size="sm"
          iconName="Download"
        >
          Export CSV
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground font-caption">Current Output</span>
            <Icon name="Package" size={18} color="var(--color-primary)" />
          </div>
          <p className="text-2xl md:text-3xl font-heading font-semibold text-foreground font-data">1,450</p>
          <p className="text-xs text-success font-caption mt-1">+5.1% vs target</p>
        </div>

        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground font-caption">Completion Rate</span>
            <Icon name="CheckCircle2" size={18} color="var(--color-success)" />
          </div>
          <p className="text-2xl md:text-3xl font-heading font-semibold text-foreground font-data">72%</p>
          <p className="text-xs text-muted-foreground font-caption mt-1">864 projects on time</p>
        </div>

        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground font-caption">Avg Delay</span>
            <Icon name="Clock" size={18} color="var(--color-warning)" />
          </div>
          <p className="text-2xl md:text-3xl font-heading font-semibold text-foreground font-data">3.0d</p>
          <p className="text-xs text-warning font-caption mt-1">-0.2d vs last month</p>
        </div>

        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground font-caption">Rework Rate</span>
            <Icon name="RotateCcw" size={18} color="var(--color-error)" />
          </div>
          <p className="text-2xl md:text-3xl font-heading font-semibold text-foreground font-data">13.2%</p>
          <p className="text-xs text-error font-caption mt-1">156 rework instances</p>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card rounded-lg border border-border p-4 md:p-6">
          <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4">Throughput Trend</h3>
          <div className="h-64 md:h-80" aria-label="Throughput Trend Chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={throughputData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis 
                  dataKey="month" 
                  tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }}
                />
                <YAxis 
                  tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'var(--color-popover)', 
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="output" 
                  stroke="var(--color-primary)" 
                  fill="var(--color-primary)"
                  fillOpacity={0.2}
                  strokeWidth={2}
                  name="Actual Output"
                />
                <Line 
                  type="monotone" 
                  dataKey="target" 
                  stroke="var(--color-secondary)" 
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  name="Target"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-4 md:p-6">
          <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4">Delay Trend Analysis</h3>
          <div className="h-64 md:h-80" aria-label="Delay Trend Bar Chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={delayTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis 
                  dataKey="week" 
                  tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }}
                />
                <YAxis 
                  yAxisId="left"
                  tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }}
                  label={{ value: 'Count', angle: -90, position: 'insideLeft', fill: 'var(--color-muted-foreground)' }}
                />
                <YAxis 
                  yAxisId="right"
                  orientation="right"
                  tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }}
                  label={{ value: 'Days', angle: 90, position: 'insideRight', fill: 'var(--color-muted-foreground)' }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'var(--color-popover)', 
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Bar yAxisId="left" dataKey="delays" fill="var(--color-warning)" radius={[8, 8, 0, 0]} name="Delay Count" />
                <Line yAxisId="right" type="monotone" dataKey="avgDelay" stroke="var(--color-error)" strokeWidth={2} name="Avg Delay (days)" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4">Bottleneck Areas</h3>
        <div className="space-y-4">
          {bottleneckAreas?.map((bottleneck, index) => (
            <div key={index} className="p-4 rounded-md border border-border bg-muted/30">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-sm font-semibold text-foreground">{bottleneck?.area}</h4>
                    <span 
                      className="px-2 py-0.5 rounded text-xs font-semibold font-caption"
                      style={{ 
                        backgroundColor: `${getImpactColor(bottleneck?.impact)}20`,
                        color: getImpactColor(bottleneck?.impact)
                      }}
                    >
                      {bottleneck?.impact} Impact
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground font-caption">
                    <span className="flex items-center gap-1">
                      <Icon name="Clock" size={14} />
                      {bottleneck?.delayDays} days avg delay
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="AlertCircle" size={14} />
                      {bottleneck?.affectedProjects} projects affected
                    </span>
                  </div>
                </div>
                <Icon name="AlertTriangle" size={20} color={getImpactColor(bottleneck?.impact)} />
              </div>
              <p className="text-xs text-muted-foreground mb-3 font-caption">{bottleneck?.description}</p>
              <div className="flex items-start gap-2 p-3 rounded bg-background">
                <Icon name="Target" size={14} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
                <p className="text-xs text-foreground font-caption">{bottleneck?.action}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4">Rework / Repeat Work Indicator</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="p-4 rounded-md bg-error/10 border border-error/30">
            <p className="text-xs text-muted-foreground font-caption mb-1">Total Rework</p>
            <p className="text-2xl font-heading font-semibold text-foreground font-data">{reworkMetrics?.totalRework}</p>
          </div>
          <div className="p-4 rounded-md bg-error/10 border border-error/30">
            <p className="text-xs text-muted-foreground font-caption mb-1">Rework Rate</p>
            <p className="text-2xl font-heading font-semibold text-foreground font-data">{reworkMetrics?.reworkRate}%</p>
          </div>
          <div className="p-4 rounded-md bg-error/10 border border-error/30">
            <p className="text-xs text-muted-foreground font-caption mb-1">Cost Impact</p>
            <p className="text-2xl font-heading font-semibold text-foreground font-data">${(reworkMetrics?.costImpact / 1000)?.toFixed(0)}K</p>
          </div>
          <div className="p-4 rounded-md bg-error/10 border border-error/30">
            <p className="text-xs text-muted-foreground font-caption mb-1">Time Impact</p>
            <p className="text-2xl font-heading font-semibold text-foreground font-data">{reworkMetrics?.timeImpact}h</p>
          </div>
        </div>
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-foreground">Top Rework Reasons</h4>
          {reworkMetrics?.topReasons?.map((reason, index) => (
            <div key={index} className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-foreground">{reason?.reason}</span>
                <span className="text-sm font-semibold text-foreground font-data">{reason?.percentage}%</span>
              </div>
              <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className="h-full bg-error rounded-full transition-smooth"
                  style={{ width: `${reason?.percentage}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground font-caption">{reason?.count} instances</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ExecutionDrivers;